import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["data-test", "focusLock", "returnFocus", "removeScroll", "onClose", "onBack", "onOpen", "disableEscClose", "disableButtonClose"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import { CloseIcon, BackArrowIcon } from '@sainsburys-tech/bolt-icons';
import * as Styled from './Drawer.styles';
var ESC_KEY = 27;
var DrawerContext = /*#__PURE__*/React.createContext();
export var DrawerHeader = function DrawerHeader(_ref) {
  var children = _ref.children;

  var _useContext = useContext(DrawerContext),
      hasBackButton = _useContext.hasBackButton,
      backButtonLabel = _useContext.backButtonLabel,
      closeButtonLabel = _useContext.closeButtonLabel,
      dataTest = _useContext.dataTest,
      isStatic = _useContext.isStatic,
      onBack = _useContext.onBack,
      onClose = _useContext.onClose,
      disableButtonClose = _useContext.disableButtonClose;

  return /*#__PURE__*/React.createElement(Styled.Header, {
    isStatic: isStatic,
    hasBackButton: hasBackButton
  }, hasBackButton && /*#__PURE__*/React.createElement(Styled.BackButton, {
    type: "button",
    isStatic: isStatic,
    onClick: onBack,
    "data-test": "".concat(dataTest, "-button-back")
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, backButtonLabel), /*#__PURE__*/React.createElement(BackArrowIcon, {
    width: "24",
    height: "24"
  })), /*#__PURE__*/React.createElement("h4", {
    id: "".concat(dataTest, "-drawer-title")
  }, /*#__PURE__*/React.createElement("strong", null, children)), !disableButtonClose && /*#__PURE__*/React.createElement(Styled.CloseButton, {
    "data-autofocus": true,
    type: "button",
    isStatic: isStatic,
    onClick: onClose,
    "data-test": "".concat(dataTest, "-button-close")
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, closeButtonLabel), /*#__PURE__*/React.createElement(CloseIcon, {
    width: "24",
    height: "24"
  })));
};
export var DrawerContent = function DrawerContent(_ref2) {
  var children = _ref2.children;

  var _useContext2 = useContext(DrawerContext),
      dataTest = _useContext2.dataTest,
      className = _useContext2.className,
      height = _useContext2.height,
      isStatic = _useContext2.isStatic,
      padding = _useContext2.padding,
      smoothScrolling = _useContext2.smoothScrolling;

  return /*#__PURE__*/React.createElement(Styled.Content, {
    padding: padding,
    height: height,
    isStatic: isStatic,
    className: "".concat(className ? "".concat(className, "-content") : ''),
    "data-test": "".concat(dataTest, "-content"),
    smoothScrolling: smoothScrolling
  }, children);
};
export var DrawerFooter = function DrawerFooter(_ref3) {
  var children = _ref3.children;

  var _useContext3 = useContext(DrawerContext),
      dataTest = _useContext3.dataTest;

  return /*#__PURE__*/React.createElement(Styled.Footer, {
    "data-test": "".concat(dataTest, "-footer")
  }, children);
};
export var Drawer = function Drawer(_ref4) {
  var dataTest = _ref4['data-test'],
      focusLock = _ref4.focusLock,
      returnFocus = _ref4.returnFocus,
      removeScroll = _ref4.removeScroll,
      onClose = _ref4.onClose,
      onBack = _ref4.onBack,
      onOpen = _ref4.onOpen,
      disableEscClose = _ref4.disableEscClose,
      disableButtonClose = _ref4.disableButtonClose,
      props = _objectWithoutProperties(_ref4, _excluded);

  var _useState = useState(props.show),
      _useState2 = _slicedToArray(_useState, 2),
      show = _useState2[0],
      toggleDrawer = _useState2[1];

  var hasCustomReturnFocus = typeof returnFocus === 'string';
  useEffect(function () {
    var handleKeyboardClose = function handleKeyboardClose(evt) {
      if (show && evt.keyCode === ESC_KEY && !disableEscClose) onClose();
    };

    window.addEventListener('keydown', handleKeyboardClose);
    return function () {
      window.removeEventListener('keydown', handleKeyboardClose); // eslint-disable-next-line no-unused-expressions

      hasCustomReturnFocus && document.querySelector(returnFocus).focus();
    };
  }, [show, disableEscClose]);
  useEffect(function () {
    if (props.show) {
      onOpen();
    }

    toggleDrawer(props.show);
  }, [props.show]);
  var focusLockDisabled = props.isStatic || !focusLock || !show;
  var removeScrollEnabled = !props.isStatic && removeScroll && show;
  var ariaLabelledBy = props.ariaLabelledBy ? props.ariaLabelledBy : "".concat(dataTest, "-drawer-title");
  return /*#__PURE__*/React.createElement(DrawerContext.Provider, {
    value: _objectSpread({
      dataTest: dataTest,
      onClose: onClose,
      onBack: onBack,
      onOpen: onOpen,
      disableButtonClose: disableButtonClose
    }, props)
  }, /*#__PURE__*/React.createElement(FocusLock, {
    disabled: focusLockDisabled,
    returnFocus: !hasCustomReturnFocus
  }, /*#__PURE__*/React.createElement(RemoveScroll, {
    enabled: removeScrollEnabled
  }, /*#__PURE__*/React.createElement(Styled.Wrapper, {
    show: show,
    className: props.className,
    isStatic: props.isStatic
  }, /*#__PURE__*/React.createElement(Styled.Container, _extends({
    show: show,
    position: props.position,
    role: "dialog",
    "aria-modal": !!props.isStatic,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": props.ariaDescribedBy,
    size: props.size,
    isStatic: props.isStatic,
    "data-test": dataTest,
    hasBorder: props.hasBorder
  }, props), show && /*#__PURE__*/React.createElement(React.Fragment, null, props.heading && /*#__PURE__*/React.createElement(DrawerHeader, null, props.heading), props.heading || props.footer ? /*#__PURE__*/React.createElement(DrawerContent, null, props.children) : props.children, props.footer && /*#__PURE__*/React.createElement(DrawerFooter, null, props.footer))), /*#__PURE__*/React.createElement(Styled.Overlay, {
    show: show,
    isStatic: props.isStatic,
    onClick: onClose,
    "data-test": "".concat(dataTest, "-overlay")
  })))));
};
Drawer.displayName = 'Drawer';
DrawerHeader.displayName = 'DrawerHeader';
DrawerContent.displayName = 'DrawerContent';
DrawerFooter.displayName = 'DrawerFooter';
Drawer.propTypes = {
  /**
   * Optional prop to establish relationships between drawer and it's trigger element via an `#id`
   */
  ariaLabelledBy: PropTypes.string,

  /**
   * Optional prop to associate drawer with a descriptive paragraph via the elements `#id`
   */
  ariaDescribedBy: PropTypes.string,

  /**
   * Optional prop to show a back '<-' button
   */
  hasBackButton: PropTypes.bool,

  /**
   * Optional prop to override the accessible back button label
   */
  backButtonLabel: PropTypes.string,

  /**
   * Optional prop to override the accessible close button label
   */
  closeButtonLabel: PropTypes.string,

  /**
   * Specify the content of your Drawer
   */
  children: PropTypes.node,

  /**
   * Specify an optional className to be added to your Drawer
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Drawer for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Specify an optional focusLock bool prop to disable/enable focusing outside of the modal
   */
  focusLock: PropTypes.bool,

  /**
   * Specify an optional element selector to return focus to (by default this uses document.activeElement at time of modal opening)
   * Useful for server side rendering when return focus needs to be persisted
   */
  returnFocus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),

  /**
   * Optional prop to specify the heading for the Drawer [DEPRECATED] use declarative <DrawerHeader />
   */
  heading: PropTypes.string,

  /**
   * Required prop to allow the Drawer to be dismissed/closed
   */
  show: PropTypes.bool.isRequired,

  /**
   * Optional callback fn prop fired when drawer is opened
   */
  onOpen: PropTypes.func,

  /**
   * Optional callback fn prop fired when drawer is closed
   */
  onClose: PropTypes.func,

  /**
   * Optional callback fn prop fired when drawer back button is used
   */
  onBack: PropTypes.func,

  /**
   * Optional prop to disable/enable padding around the content
   */
  padding: PropTypes.bool,

  /**
   * Optional prop to disable/enable padding around the content
   */
  position: PropTypes.string,

  /**
   * Optional prop to specify the height of the Drawer
   */
  height: PropTypes.oneOf(['auto', 'viewheight']),

  /**
   * Optional prop to specify if the Drawer is static
   */
  isStatic: PropTypes.bool,

  /**
   * Optional prop to specify the width of the Drawer
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'full']),

  /**
   * Optional prop to enable smooth scrolling within the scrollable content area. e.g. when programatically scrolling to a set position or focusing an element
   */
  smoothScrolling: PropTypes.bool,

  /**
   * Optional prop to disable scrolling of the page behind the drawer
   */
  removeScroll: PropTypes.bool,

  /**
   * Optional prop to specify a footer for the Drawer [DEPRECATED] use declarative <DrawerFooter />
   */
  footer: PropTypes.node,

  /**
   * Optional prop to specify if the border exists or not for a static Drawer
   */
  hasBorder: PropTypes.bool,

  /**
   * Optional prop to flag whether to disable closing the modal with the escape key
   */
  disableEscClose: PropTypes.bool,

  /**
   * Optional prop to show or hide the close button
   */
  disableButtonClose: PropTypes.bool
};
Drawer.defaultProps = {
  ariaLabelledBy: null,
  ariaDescribedBy: null,
  focusLock: true,
  returnFocus: true,
  hasBackButton: false,
  backButtonLabel: 'Click here or press enter to return to the previous dialog',
  closeButtonLabel: 'Click here or press enter to close the dialog',
  heading: null,
  show: false,
  padding: true,
  position: 'right',
  height: 'viewheight',
  isStatic: false,
  size: 'md',
  smoothScrolling: false,
  removeScroll: true,
  onOpen:
  /* istanbul ignore next */
  function onOpen() {},
  onClose:
  /* istanbul ignore next */
  function onClose() {},
  onBack:
  /* istanbul ignore next */
  function onBack() {},
  footer: null,
  'data-test': 'component-drawer',
  hasBorder: true,
  disableEscClose: false,
  disableButtonClose: false
};
export default Drawer;